'use client';

import { JobPortalOffersFilter } from '@eliadmin/core/src/components/jobportal/homepage/jobportal-offers-filter';
import {
  useJobportalOffersStore,
  type JobportalOffersFilter,
} from '@eliadmin/core/src/stores/jobportal-offers-store';
import {
  JobPortalOffersStore,
  JobPortalOffersStoreSkeleton,
} from '@eliadmin/core/src/components/jobportal/offers/jobportal-offers-container';
import { Card } from '@eliadmin/core/src/components/ui/card';
import { Container } from '@eliadmin/core/src/components/ui/container';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Suspense, useRef, useState } from 'react';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export function JobPortalOffersContentInner() {
  const { t } = useTranslations();
  const storeFilter = useJobportalOffersStore(s => s.filter);
  const setFilter = useJobportalOffersStore(s => s.setFilter);
  const clearFilters = useJobportalOffersStore(s => s.clearFilters);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [draftFilter, setDraftFilter] = useState<JobportalOffersFilter>(storeFilter ?? {});
  const skipApplyOnCloseRef = useRef(false);

  const applyDraft = () => {
    clearFilters();
    setFilter(draftFilter);
  };

  return (
    <section
      className={cn(
        'custom-section',
        'sm.pb-32 ms:pb-40 pb-24 pt-8 sm:pt-12 md:pt-20 lg:pb-52 lg:pt-28',
      )}
    >
      <div
        className={cn('custom-container', 'flex flex-col gap-8', 'ea-job-portal-offers__container')}
      >
        <div className="md:hidden">
          <Sheet
            open={sheetOpen}
            onOpenChange={open => {
              if (open) {
                setDraftFilter(storeFilter ?? {});
              }
              if (!open && sheetOpen) {
                if (!skipApplyOnCloseRef.current) {
                  applyDraft();
                }
                skipApplyOnCloseRef.current = false;
              }
              setSheetOpen(open);
            }}
          >
            <SheetTrigger asChild>
              <Button variant="secondary" className="w-full">
                {t('text174')}
              </Button>
            </SheetTrigger>
            <SheetContent side="full" className="p-0">
              <div className="flex h-full flex-col">
                <div className="flex-1 overflow-y-auto px-4 pb-6 pt-8">
                  <Card variant="outlined" className="flex flex-col gap-8 rounded-lg p-4">
                    <JobPortalOffersFilter
                      value={draftFilter}
                      onChange={patch => {
                        setDraftFilter(prev => ({ ...prev, ...patch }));
                      }}
                      showClearButton={false}
                    />
                  </Card>
                </div>
                <div className="sticky bottom-0 z-10 border-t bg-background px-4 py-3">
                  <div className="flex gap-2">
                    <SheetClose asChild>
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                          clearFilters();
                          setDraftFilter({});
                          skipApplyOnCloseRef.current = true;
                        }}
                      >
                        {t('text2476')}
                      </Button>
                    </SheetClose>
                    <SheetClose asChild>
                      <Button
                        className="w-full"
                        onClick={() => {
                          applyDraft();
                          skipApplyOnCloseRef.current = true;
                        }}
                      >
                        {t('text746')}
                      </Button>
                    </SheetClose>
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
        <div
          className={cn(
            'flex w-full flex-col items-start justify-start gap-6 md:flex-row md:items-start md:justify-between',
            'ea-job-portal-offers__grid',
          )}
        >
          <div
            className={cn('hidden w-full max-w-[362px] md:block', 'ea-job-portal-offers__filter')}
          >
            <Card variant="outlined" className="flex flex-col gap-8 rounded-[6px] p-4">
              <JobPortalOffersFilter />
            </Card>
          </div>
          <div className={cn('w-full flex-1', 'ea-job-portal-offers')}>
            <Suspense fallback={<JobPortalOffersStoreSkeleton />}>
              <JobPortalOffersStore showLoadMore />
            </Suspense>
          </div>
        </div>
      </div>
    </section>
  );
}
